import { Button, Modal } from "@hatch-digital-nl/bravilor-storybook";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { setIsSyncDataModalOpen } from "./SyncDataModalSlice";
import { useDataSync } from "../../common/hooks/useDataSync";
import { useLocalData } from "../../common/hooks/useLocalData";
import { useNetworkStatus } from "../../common/hooks/useNetworkStatus";
import { useContext, JSX } from "react";
import { AuthContext } from "react-oauth2-code-pkce";

export const SyncDataModalContainer = (): JSX.Element | null => {
    const dispatch = useAppDispatch();

    const authContext = useContext(AuthContext) || null;

    const token = authContext?.token;
    const logIn = authContext?.logIn || (() => {});

    const { syncData, isLoading, progress } = useDataSync();
    const { isSyncDataModalOpen } = useAppSelector(
        (state) => state.syncDataModal,
    );
    const { refreshData } = useLocalData();
    const { isOnline, isOffline } = useNetworkStatus();

    if (!isSyncDataModalOpen) return null;

    const handleCloseModal = (): void => {
        dispatch(setIsSyncDataModalOpen(false));
    };

    const handleSync = async (): Promise<void> => {
        if (!isOnline) {
            return;
        }
        if (!token) {
            logIn();
            return;
        }

        try {
            await syncData();
            await refreshData();
            handleCloseModal();
        } catch (error: unknown) {
            const apiError = error as {
                status?: number;
                data?: { status?: number };
            };
            if (apiError?.status === 401 || apiError?.data?.status === 401) {
                console.log(
                    "Authentication expired during sync, redirecting to login",
                );
                logIn();
                return;
            }

            console.error("Sync failed:", error);
            handleCloseModal();
        }
    };

    return (
        <Modal onClose={!isLoading ? handleCloseModal : undefined}>
            <div className={"c-sync-data-modal"}>
                {!isLoading ? (
                    <>
                        <h1 className={"c-sync-data-modal__title"}>
                            {isOffline
                                ? "No Internet Connection"
                                : !token
                                  ? "Authentication Required"
                                  : "Ensure Stable Connection"}
                        </h1>
                        {isOffline ? (
                            <p
                                className={"c-sync-data-modal__text"}
                                style={{ color: "#ff6b6b" }}
                            >
                                You are currently offline. Please connect to the
                                internet to sync the latest product data.
                            </p>
                        ) : !token ? (
                            <p
                                className={"c-sync-data-modal__text"}
                                style={{ color: "#ff6b6b" }}
                            >
                                You need to be logged in to sync the latest
                                product data. Please authenticate first.
                            </p>
                        ) : (
                            <p className={"c-sync-data-modal__text"}>
                                Please ensure you have a stable internet
                                connection before proceeding with the update. A
                                poor connection might cause interruptions or
                                failure during the update process.
                            </p>
                        )}
                    </>
                ) : (
                    <>
                        <h1 className={"c-sync-data-modal__title"}>
                            Data is synchronizing...
                        </h1>
                        <p className={"c-sync-data-modal__text"}>
                            We're downloading the latest product catalog,
                            including specifications, and availability
                            information.
                        </p>
                        <p className={"c-sync-data-modal__text"}>
                            This may take a few minutes, depending on your
                            connection speed.
                        </p>
                        {isOffline && (
                            <p
                                className={"c-sync-data-modal__text"}
                                style={{ color: "#ff6b6b" }}
                            >
                                Warning: Connection lost during sync. Please
                                check your internet connection.
                            </p>
                        )}
                    </>
                )}
                {progress && (
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            gap: "1rem",
                            margin: "2rem 0",
                        }}
                    >
                        <div
                            style={{
                                width: "200px",
                                height: "8px",
                                backgroundColor: "#e0e0e0",
                                borderRadius: "4px",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    width: `${progress.progress || 0}%`,
                                    height: "100%",
                                    backgroundColor: "#4CAF50",
                                    transition: "width 0.3s ease",
                                    borderRadius: "4px",
                                }}
                            />
                        </div>
                        <p style={{ fontSize: "1.2rem", fontWeight: "600" }}>
                            {progress.progress}%
                        </p>
                    </div>
                )}
                {!isLoading && (
                    <div className={"c-sync-data-modal__buttons"}>
                        <Button
                            disabled={isLoading || isOffline}
                            variant={"primary"}
                            textColor={"#000"}
                            backgroundColor={isOffline ? "#ccc" : "#C9B2A0"}
                            onClick={handleSync}
                        >
                            {isOffline
                                ? "No internet connection"
                                : !token
                                  ? "Login to sync"
                                  : "Yes, i have a stable connection"}
                        </Button>
                        <Button
                            disabled={isLoading}
                            variant={"secondary"}
                            onClick={handleCloseModal}
                        >
                            Cancel
                        </Button>
                    </div>
                )}
            </div>
        </Modal>
    );
};
